import React from 'react';
import { Box, Typography } from '@mui/material';
import { useBranding } from '../hooks/useBranding';
import { DEFAULT_BRANDING } from '../constants/branding';

type Variant = 'login' | 'sidebar' | 'appbar';

interface Props {
  variant?: Variant;
  showSubtitle?: boolean;
}

const LOGO_SIZE: Record<Variant, number> = {
  login: 72,
  sidebar: 40,
  appbar: 32,
};

const AppBrand: React.FC<Props> = ({ variant = 'sidebar', showSubtitle }) => {
  const { branding } = useBranding();
  const size = LOGO_SIZE[variant];
  const subtitle =
    showSubtitle ?? (variant === 'login' || variant === 'sidebar');
  const logoSrc = branding.logo_url || DEFAULT_BRANDING.logoUrl;
  const title = variant === 'appbar' ? branding.site_short_title : branding.site_title;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: variant === 'login' ? 2 : 1.25,
        flexDirection: variant === 'login' ? 'column' : 'row',
        textAlign: variant === 'login' ? 'center' : 'left',
        width: variant === 'sidebar' ? '100%' : 'auto',
        minWidth: 0,
      }}
    >
      <Box
        component="img"
        src={logoSrc}
        alt={`Logo ${branding.site_title}`}
        sx={{
          width: size,
          height: size,
          objectFit: 'contain',
          flexShrink: 0,
        }}
        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
          const img = e.currentTarget;
          if (img.src !== DEFAULT_BRANDING.logoUrl) {
            img.src = DEFAULT_BRANDING.logoUrl;
          } else {
            img.style.display = 'none';
          }
        }}
      />
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography
          component="div"
          sx={{
            fontWeight: 700,
            lineHeight: 1.2,
            fontSize:
              variant === 'login' ? '1.75rem' : variant === 'appbar' ? '1rem' : '0.95rem',
            color: variant === 'sidebar' ? 'primary.main' : 'inherit',
          }}
          noWrap={variant !== 'login'}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              lineHeight: 1.3,
              mt: 0.25,
              fontSize: variant === 'login' ? '0.95rem' : '0.7rem',
            }}
            noWrap={variant === 'sidebar'}
          >
            {branding.site_subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AppBrand;
